import { useMemo, useState } from 'react';
import type { DraftState, TeamLiveData } from '../types';
import type { TeamValuation } from '../lib/valuation';
import { TEAMS_BY_ABBR, teamFullName } from '../constants/teams';
import { BASELINE_PROJECTIONS } from '../constants/teamProjections';
import { TeamLogo } from './TeamLogo';
import { SuggestedValueBadge } from './SuggestedValueBadge';

type SortKey = 'value' | 'name' | 'lastPrice' | 'proj' | 'fpi' | 'rank' | 'record' | 'sos';

interface Props {
  state: DraftState;
  liveData: Record<string, TeamLiveData>;
  rankings: Record<string, number>;
  valuations: Record<string, TeamValuation>;
  pastPrices: Record<string, number>;
  canNominate: boolean;
  localMaxBid: number;
  onNominate: (abbr: string) => void;
}

/** Sortable cheat-sheet: one row per available team with all live signals. */
export function CheatSheet({
  state, liveData, rankings, valuations, pastPrices,
  canNominate, localMaxBid, onNominate,
}: Props) {
  const [sortKey, setSortKey] = useState<SortKey>('value');
  const [asc, setAsc] = useState(false);
  const [includeAll, setIncludeAll] = useState(false);

  const rows = useMemo(() => {
    const abbrs = includeAll
      ? Object.keys(TEAMS_BY_ABBR)
      : state.availableTeams;
    const arr = abbrs.map(abbr => {
      const meta = TEAMS_BY_ABBR[abbr];
      const live = liveData[abbr];
      const base = BASELINE_PROJECTIONS[abbr];
      const v = valuations[abbr];
      return {
        abbr,
        name: teamFullName(abbr),
        meta,
        live,
        valuation: v,
        lastPrice: pastPrices[abbr] ?? null,
        proj: live?.projectedWins ?? base?.projectedWins ?? null,
        fpi: live?.fpi ?? base?.fpi ?? null,
        fpiRank: live?.fpiRank ?? base?.fpiRank ?? null,
        sos: live?.sosRank ?? base?.sosRank ?? null,
        rank: rankings[abbr] ?? base?.fpiRank ?? null,
        record: live?.record,
        taken: !state.availableTeams.includes(abbr),
      };
    });
    arr.sort((a, b) => {
      const cmp = (() => {
        switch (sortKey) {
          case 'name': return a.name.localeCompare(b.name);
          case 'lastPrice': return (a.lastPrice ?? -1) - (b.lastPrice ?? -1);
          case 'proj': return (a.proj ?? -1) - (b.proj ?? -1);
          case 'fpi': return (a.fpi ?? -999) - (b.fpi ?? -999);
          case 'rank': return (b.rank ?? 33) - (a.rank ?? 33); // higher rank = lower
          case 'sos': return (b.sos ?? 33) - (a.sos ?? 33);     // lower SOS rank = harder schedule
          case 'record': {
            const aw = a.record ? (a.record.wins + 0.5 * a.record.ties) : -1;
            const bw = b.record ? (b.record.wins + 0.5 * b.record.ties) : -1;
            return aw - bw;
          }
          case 'value':
          default:
            return (a.valuation?.suggested ?? 0) - (b.valuation?.suggested ?? 0);
        }
      })();
      return asc ? cmp : -cmp;
    });
    return arr;
  }, [includeAll, state.availableTeams, liveData, valuations, pastPrices, rankings, sortKey, asc]);

  const setSort = (k: SortKey) => {
    if (k === sortKey) setAsc(!asc);
    else { setSortKey(k); setAsc(k === 'name'); /* names asc by default */ }
  };

  const arrow = (k: SortKey) => (sortKey === k ? (asc ? ' ↑' : ' ↓') : '');

  return (
    <div className="panel p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <h2 className="text-lg font-extrabold text-white">📊 Cheat Sheet</h2>
          <p className="text-[10px] text-slate-500">
            Live FPI projections + last-year prices · your max bid: <span className="text-teal-300 font-bold">${localMaxBid}</span>
          </p>
        </div>
        <div className="flex gap-1 text-xs">
          <button
            onClick={() => setIncludeAll(false)}
            className={`px-2 py-1 rounded ${!includeAll ? 'bg-teal-500 text-slate-900 font-bold' : 'bg-slate-700 text-slate-300'}`}
          >Available</button>
          <button
            onClick={() => setIncludeAll(true)}
            className={`px-2 py-1 rounded ${includeAll ? 'bg-teal-500 text-slate-900 font-bold' : 'bg-slate-700 text-slate-300'}`}
          >All 32</button>
        </div>
      </div>

      <div className="overflow-x-auto -mx-2">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-[10px] uppercase text-slate-400 border-b border-slate-700">
              <Th onClick={() => setSort('name')}>Team{arrow('name')}</Th>
              <Th onClick={() => setSort('value')} center>Suggest ${arrow('value')}</Th>
              <Th onClick={() => setSort('lastPrice')} center>Last ${arrow('lastPrice')}</Th>
              <Th onClick={() => setSort('proj')} center title="ESPN projected regular-season wins (FPI)">Proj W{arrow('proj')}</Th>
              <Th onClick={() => setSort('fpi')} center title="ESPN Football Power Index rating">FPI{arrow('fpi')}</Th>
              <Th onClick={() => setSort('record')} center>Rec{arrow('record')}</Th>
              <Th onClick={() => setSort('rank')} center>Rk{arrow('rank')}</Th>
              <Th onClick={() => setSort('sos')} center title="ESPN strength-of-schedule rank — lower = tougher slate">SOS{arrow('sos')}</Th>
              <Th center>Action</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const v = r.valuation;
              const affordable = v && v.suggested <= localMaxBid;
              const bargain = v && r.lastPrice != null && v.suggested > r.lastPrice;
              return (
                <tr
                  key={r.abbr}
                  className={`border-b border-slate-800 hover:bg-slate-800/40 ${r.taken ? 'opacity-30' : ''}`}
                  style={{ borderLeft: `3px solid ${r.meta?.color ?? '#475569'}` }}
                >
                  <td className="py-1.5 px-2">
                    <div className="flex items-center gap-2">
                      <TeamLogo abbr={r.abbr} size={22} />
                      <span className="font-bold text-white">{r.abbr}</span>
                      <span className="hidden sm:inline text-slate-400">{r.meta?.name}</span>
                    </div>
                  </td>
                  <td className="text-center px-2">
                    {v && (
                      <SuggestedValueBadge
                        v={v}
                        label="≈"
                        className={affordable ? '' : 'opacity-60'}
                      />
                    )}
                  </td>
                  <td className="text-center px-2">
                    {r.lastPrice != null ? (
                      <span className={`font-bold ${bargain ? 'text-emerald-400' : 'text-amber-300'}`}>${r.lastPrice}</span>
                    ) : <span className="text-slate-600">—</span>}
                  </td>
                  <td className="text-center px-2">
                    {r.proj != null ? <span className="text-slate-200">{r.proj.toFixed(1)}</span> : <span className="text-slate-600">—</span>}
                  </td>
                  <td className="text-center px-2">
                    {r.fpi != null
                      ? <span className={r.fpi >= 0 ? 'text-emerald-400' : 'text-rose-400'}>{r.fpi.toFixed(1)}</span>
                      : <span className="text-slate-600">—</span>}
                  </td>
                  <td className="text-center px-2 text-slate-300">
                    {r.record ? `${r.record.wins}-${r.record.losses}${r.record.ties ? `-${r.record.ties}` : ''}` : '—'}
                  </td>
                  <td className="text-center px-2 text-slate-400">
                    {r.rank ? `#${r.rank}` : '—'}
                  </td>
                  <td
                    className={`text-center px-2 ${r.sos != null && r.sos <= 10 ? 'text-rose-400' : r.sos != null && r.sos >= 23 ? 'text-emerald-400' : 'text-slate-400'}`}
                    title={r.sos != null ? (r.sos <= 10 ? 'Tough schedule' : r.sos >= 23 ? 'Easy schedule' : 'Average schedule') : ''}
                  >
                    {r.sos != null ? `#${r.sos}` : '—'}
                  </td>
                  <td className="text-center px-1">
                    {!r.taken && canNominate ? (
                      <button
                        onClick={() => onNominate(r.abbr)}
                        className="text-[10px] px-2 py-1 bg-teal-500 hover:bg-teal-400 text-slate-900 rounded font-bold"
                      >Nominate</button>
                    ) : r.taken ? (
                      <span className="text-[10px] text-slate-500">drafted</span>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children, onClick, center, title }: {
  children: React.ReactNode; onClick?: () => void; center?: boolean; title?: string;
}) {
  return (
    <th
      onClick={onClick}
      title={title}
      className={`py-2 px-2 ${onClick ? 'cursor-pointer select-none hover:text-teal-300' : ''} ${center ? 'text-center' : ''}`}
    >
      {children}
    </th>
  );
}
